<script lang="ts">
	import { Text, Button, Stack, toast } from '@Shelly/shelly-design-system';
	import { profile_data } from 'src/stores/account/user';
	import { dashPromoEmptyStateEnabled } from 'src/stores/features/dev/dashboard-promo-slots';
	import { verivoxDevForceEnable } from 'src/stores/features/dev/verivox-dev-force-enable';
	import {
		DASH_PROMO_MAX_BANNERS_DEFAULT,
		DASH_PROMO_SAMPLE_CATALOG,
		addDashPromoSample,
		clearAllDashPromoSampleDismissals,
		clearDashPromoBannerDismissal,
		dashPromoActiveSampleIds,
		dashPromoDismissals,
		dashPromoMaxBanners,
		removeDashPromoSample,
	} from 'src/stores/features/dev/dashboard-promo-samples';
	import { isVerivoxEligibleForDashPromos } from 'src/helpers/verivox_promo_helpers';
	import {
		clearVerivoxSurfaceDismissal,
		getVerivoxDismissalEntry,
		type VerivoxDismissSurface,
	} from 'src/components/PromotionalIntegrations/Verivox/utils/verivoxDismissals';

	/**
	 * Shared Promotions pool controls for GlobalInlineBanners + BannersWidget.
	 * Catalog, max count, empty-state preview, and sample / Verivox dismissals.
	 */

	/** Surfaces used by BannersWidget + GlobalInlineBanners. */
	const VERIVOX_PROMO_SURFACES: VerivoxDismissSurface[] = [
		'energyDashboard',
		'customDashboard',
		'roomsList',
	];

	const formatDismissedAt = (ts: number): string => {
		try {
			return new Date(ts).toLocaleString();
		} catch {
			return String(ts);
		}
	};

	$: showVerivoxInList =
		(void $profile_data, void $verivoxDevForceEnable, isVerivoxEligibleForDashPromos());

	$: verivoxDismissEntries = (() => {
		void $profile_data;
		return VERIVOX_PROMO_SURFACES.map((surface) => {
			const entry = getVerivoxDismissalEntry(surface);
			return entry?.dismissalDate ? { surface, dismissalDate: entry.dismissalDate } : null;
		}).filter(Boolean) as Array<{ surface: VerivoxDismissSurface; dismissalDate: number }>;
	})();

	$: verivoxIsDismissed = verivoxDismissEntries.length > 0;

	$: verivoxDismissLabel = verivoxDismissEntries
		.map(({ surface, dismissalDate }) => `${surface}: ${formatDismissedAt(dismissalDate)}`)
		.join(' · ');

	const resetVerivoxPromoDismissals = async () => {
		await Promise.all(
			VERIVOX_PROMO_SURFACES.map((surface) => clearVerivoxSurfaceDismissal(surface)),
		);
		toast.open({ type: 'success', title: 'Reset Verivox promo dismissals' });
	};
</script>

<Text class="mb-s text-bold">Promotions</Text>
<Text styleType="body-small" class="text-color-muted mb-s">
	Shared banner pool for <b>GlobalInlineBanners</b> and the dashboard <b>BannersWidget</b>. Sample
	campaigns are off until enabled here. Verivox is listed only for de_users / force-enable.
</Text>

<Text class="mb-s text-bold">Empty state (shop CTA)</Text>
<Stack gap="s" class="mb-s">
	<div class="d-flex flex-wrap gap-s">
		<Button
			variant={$dashPromoEmptyStateEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => dashPromoEmptyStateEnabled.set(true)}
		>
			Empty state ON
		</Button>
		<Button
			variant={!$dashPromoEmptyStateEnabled ? 'primary' : 'secondary'}
			class="width-md-xl width-sm-full"
			on:click={() => dashPromoEmptyStateEnabled.set(false)}
		>
			Empty state OFF
		</Button>
	</div>
	<Text styleType="body-small" class="text-color-muted">
		When no banners are visible, show the Shelly Shop empty state:
		<b>{$dashPromoEmptyStateEnabled ? 'on' : 'off'}</b> (default off)
	</Text>
</Stack>

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Max banners</Text>
<Stack gap="s" class="mb-s">
	<div class="d-flex flex-wrap gap-s align-items-center">
		<Button
			variant="secondary"
			class="width-md-xl width-sm-full"
			disabled={$dashPromoMaxBanners <= 1}
			on:click={() => dashPromoMaxBanners.update((n) => Math.max(1, n - 1))}
		>
			−
		</Button>
		<Text styleType="body-small">
			Limit: <b>{$dashPromoMaxBanners}</b> (default {DASH_PROMO_MAX_BANNERS_DEFAULT})
		</Text>
		<Button
			variant="secondary"
			class="width-md-xl width-sm-full"
			disabled={$dashPromoMaxBanners >= 10}
			on:click={() => dashPromoMaxBanners.update((n) => Math.min(10, n + 1))}
		>
			+
		</Button>
		<Button
			variant="secondary"
			class="width-md-xl width-sm-full"
			on:click={() => dashPromoMaxBanners.set(DASH_PROMO_MAX_BANNERS_DEFAULT)}
		>
			Reset to {DASH_PROMO_MAX_BANNERS_DEFAULT}
		</Button>
	</div>
</Stack>

<hr class="mb-m mt-m" />

<Text class="mb-s text-bold">Promo banners</Text>
<Text styleType="body-small" class="text-color-muted mb-s">
	Enable / disable catalog samples. Active:
	<b>{$dashPromoActiveSampleIds.length}</b>
	(default none)
	{#if showVerivoxInList}
		· Verivox listed while eligible
	{/if}
</Text>
<Stack gap="s" class="mb-s">
	{#if showVerivoxInList}
		<div class="d-flex align-items-center gap-s flex-wrap">
			<img
				src="./images/verivox-logo.png"
				alt=""
				width="48"
				height="28"
				style="object-fit: contain; border-radius: 4px; background: #333; padding: 2px;"
			/>
			<div class="flex-1">
				<Text styleType="body-small">
					Verivox
					{#if verivoxIsDismissed}
						<span class="text-color-muted"> · dismissed {verivoxDismissLabel}</span>
					{/if}
				</Text>
			</div>
			{#if verivoxIsDismissed}
				<Button
					variant="secondary"
					size="s"
					on:click={() => {
						void resetVerivoxPromoDismissals();
					}}
				>
					Reset
				</Button>
			{/if}
		</div>
	{/if}

	{#each DASH_PROMO_SAMPLE_CATALOG as sample (sample.id)}
		{@const enabled = $dashPromoActiveSampleIds.includes(sample.id)}
		{@const dismissedAt = $dashPromoDismissals[sample.id]}
		<div class="d-flex align-items-center gap-s flex-wrap">
			<img
				src={sample.image || './images/promotion-generic.svg'}
				alt=""
				width="48"
				height="28"
				style="object-fit: cover; border-radius: 4px; background: #333;"
			/>
			<div class="flex-1">
				<Text styleType="body-small">
					{sample.title}
					{#if dismissedAt}
						<span class="text-color-muted">
							· dismissed {formatDismissedAt(dismissedAt)}
						</span>
					{/if}
				</Text>
			</div>
			{#if dismissedAt}
				<Button
					variant="secondary"
					size="s"
					on:click={() => {
						clearDashPromoBannerDismissal(sample.id);
						toast.open({ type: 'success', title: `Reset ${sample.title}` });
					}}
				>
					Reset
				</Button>
			{/if}
			{#if enabled}
				<Button
					variant="secondary"
					size="s"
					on:click={() => {
						removeDashPromoSample(sample.id);
						toast.open({ type: 'success', title: `Disabled ${sample.title}` });
					}}
				>
					Disable
				</Button>
			{:else}
				<Button
					variant="primary"
					size="s"
					on:click={() => {
						addDashPromoSample(sample.id);
						toast.open({ type: 'success', title: `Enabled ${sample.title}` });
					}}
				>
					Enable
				</Button>
			{/if}
		</div>
	{/each}
	<Button
		variant="secondary"
		class="width-md-xl width-sm-full"
		on:click={() => {
			clearAllDashPromoSampleDismissals();
			toast.open({ type: 'success', title: 'Cleared sample banner dismissals' });
		}}
	>
		Clear sample dismissals
	</Button>
</Stack>
